import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { catchError, forkJoin, map, of } from 'rxjs';

import { CommonService } from '../../services/common.service';
import { ToastrService } from 'ngx-toastr';
import type { RoutingPipelineBlock } from '../models/routing-ruleconfig1.models';
import { RoutingRuleconfig1StoreService } from './routing-ruleconfig1-store.service';
import { routingExpressionPrep } from './routing-ruleconfig1-expression-prep.util';
import { XmlFieldPathResolverService } from './xml-field-path-resolver.service';
import { hl7DisplayKeyFromPersistedPath } from '../hl7/hl7-path-step-builder';
import { isHl7PersistedFieldValue } from '../utils/routing-conversion-type.util';

@Injectable()
export class RoutingRuleconfig1PipelineService {
  readonly pipelineKeyword = 'displayname';
  leftPipelineSearchForm = new FormGroup({
    routename: new FormControl<string | unknown>(''),
  });
  leftPipelineRouteNameList: { id: string; displayname: string }[] = [];
  leftPipelineNotFound = false;
  isPipelineLeftSearchLoading = false;
  /** Pipeline chosen from search dropdown; added to list only on Add click. */
  leftPipelinePendingSelection: { id: string; displayname: string } | null = null;
  /** Filters left pipeline list while search text is active; cleared on cancel. */
  private leftPanelListFilter:
    | { mode: 'none' }
    | { mode: 'text'; text: string }
    | { mode: 'id'; id: string } = { mode: 'none' };

  private lastLeftNgPipelineQuery = '';

  private get pipelineIdToDisplayName(): Map<string, string> {
    return this.store.pipelineIdToDisplayName;
  }

  constructor(
    private readonly store: RoutingRuleconfig1StoreService,
    private readonly commonService: CommonService,
    private readonly pathResolver: XmlFieldPathResolverService,
    private readonly toastr: ToastrService
  ) {}

  initFromConfig(
    config: unknown[] | null | undefined,
    options?: { preserveEditState?: boolean }
  ): void {
    const previousSelectedId = (this.store.selectedPipelineId ?? '').toString().trim();
    this.store.fieldDisplayToPath.clear();
    if (!options?.preserveEditState) {
      this.store.editingBlockId = null;
      this.store.editingRuleIndex = null;
      this.store.isPipelineLocked = false;
    }

    const source = Array.isArray(config) ? config : [];
    const blocks: RoutingPipelineBlock[] = [];

    source.forEach((item: unknown, index: number) => {
      const row = item as { pipeline?: string; rules?: unknown[]; ruleSatisfaction?: string };
      const pipelineId = (row?.pipeline ?? '').toString().trim();
      if (!pipelineId) {
        return;
      }
      const rules = (Array.isArray(row.rules) ? row.rules : []).map((rule: unknown, ri: number) => {
        const r = rule as Record<string, unknown>;
        return {
          rulenumber: Number(r.rulenumber ?? ri + 1),
          rulename: this.store.getRuleNameFromRule(r as never, Number(r.rulenumber ?? ri + 1)),
          field: (r.field ?? '').toString(),
          operation: this.store.mapOperatorToUi(r.operator ?? r.operation),
          value: (r.value ?? '').toString(),
          delimiter: (r.delimiter ?? '|').toString(),
        };
      });

      const block: RoutingPipelineBlock = {
        id: pipelineId,
        label: this.pipelineIdToDisplayName.get(pipelineId) || pipelineId,
        rules,
        ruleSatisfaction: (row.ruleSatisfaction ?? '').toString(),
        expanded: index === 0,
      };
      const namedSat = routingExpressionPrep.resolveFinalExpressionForBlock(block);
      if (namedSat) {
        block.ruleSatisfaction = namedSat;
      }
      blocks.push(block);
    });

    this.store.patchBlocks(blocks);
    if (!blocks.length) {
      this.store.selectedPipelineId = null;
      this.leftPipelineRouteNameList = [];
      this.leftPipelineSearchForm.patchValue({ routename: '' }, { emitEvent: false });
      return;
    }
    this.primeFieldMapsFromRules();
    const resolvedSelectedId =
      (previousSelectedId && blocks.some((b) => b.id === previousSelectedId))
        ? previousSelectedId
        : blocks[0].id;
    this.store.selectedPipelineId = resolvedSelectedId;
    this.store.ruleForm.patchValue({ pipeline: resolvedSelectedId }, { emitEvent: false });
    this.resolvePipelineDisplayNames(blocks.map((b) => b.id));
  }

  selectPipelineBlock(block: RoutingPipelineBlock): void {
    this.store.selectedPipelineId = block.id;
    this.store.editingBlockId = null;
    this.store.editingRuleIndex = null;
    this.leftPipelinePendingSelection = null;
    this.leftPanelListFilter = { mode: 'none' };
    this.store.ruleForm.patchValue(
      { pipeline: block.id, rulename: '', field: '', operator: '', value: '' },
      { emitEvent: false }
    );
    this.leftPipelineSearchForm.patchValue({ routename: '' }, { emitEvent: false });
    block.expanded = true;
    this.store.patchBlocks([...this.store.pipelineBlocks]);
  }

  onClickNewPipelineFromLeft(): void {
    const id = this.resolveLeftPipelinePendingId();
    if (!id) {
      this.toastr.warning('Search and select a pipeline, then click Add.', '', {
        timeOut: 2500,
        positionClass: 'toast-bottom-right',
      });
      return;
    }
    const existing = this.store.pipelineBlocks.find(
      (b) => b.id.toLowerCase() === id.toLowerCase()
    );
    if (existing) {
      this.toastr.warning('Pipeline is already in the list. Click it below to open rules.', '', {
        timeOut: 2500,
        positionClass: 'toast-bottom-right',
      });
      return;
    }
    const displayname =
      this.leftPipelinePendingSelection?.displayname ||
      this.pipelineIdToDisplayName.get(id) ||
      id;
    const block: RoutingPipelineBlock = {
      id,
      label: displayname,
      rules: [],
      ruleSatisfaction: '',
      expanded: true,
    };
    this.store.pipelineBlocks.unshift(block);
    this.store.patchBlocks([...this.store.pipelineBlocks]);
    this.leftPipelinePendingSelection = null;
  }

  onLeftNgPipelineInputChanged(val: unknown): void {
    const event = (val ?? '').toString();
    if (
      this.leftPipelinePendingSelection &&
      event !== this.leftPipelinePendingSelection.displayname
    ) {
      this.leftPipelinePendingSelection = null;
    }
    if (!event) {
      this.leftPipelineRouteNameList = [];
      this.isPipelineLeftSearchLoading = false;
      this.leftPipelineNotFound = false;
      this.leftPipelinePendingSelection = null;
      this.leftPanelListFilter = { mode: 'none' };
      return;
    }
    this.leftPanelListFilter = { mode: 'text', text: event };
    this.lastLeftNgPipelineQuery = event;
    if (event.length <= 3) {
      this.leftPipelineRouteNameList = [];
      this.isPipelineLeftSearchLoading = false;
      this.leftPipelineNotFound = false;
      return;
    }
    this.isPipelineLeftSearchLoading = true;
    const url = `api/Routes/GetByDisplayName?name=${encodeURIComponent(event)}&version=2`;
    this.commonService.getapiurl(url).subscribe({
      next: (data: unknown) => {
        this.isPipelineLeftSearchLoading = false;
        if (this.lastLeftNgPipelineQuery !== event) {
          return;
        }
        const rows = Array.isArray(data) ? data : [];
        const mapped = rows.map((obj: Record<string, unknown>) => ({
          id: String(obj.id ?? '').trim(),
          displayname: `${obj.displayname ?? ''} (${obj.id ?? ''})`.trim(),
        }));
        this.leftPipelineRouteNameList = mapped;
        this.leftPipelineNotFound = mapped.length === 0;
        for (const r of mapped) {
          if (r.id) {
            this.pipelineIdToDisplayName.set(r.id, r.displayname);
          }
        }
        this.applyLabelsToBlocks();
      },
      error: () => {
        this.isPipelineLeftSearchLoading = false;
        this.leftPipelineRouteNameList = [];
        this.toastr.error('Unable to fetch the Pipelines', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      },
    });
  }

  onLeftNgPipelineSelected(item: { id?: string; displayname?: string }): void {
    const id = (item?.id ?? '').toString().trim();
    if (!id) {
      return;
    }
    const displayname = (item?.displayname ?? '').toString().trim() || id;
    this.pipelineIdToDisplayName.set(id, displayname);
    this.leftPipelinePendingSelection = { id, displayname };
    const existing = this.store.pipelineBlocks.find(
      (b) => b.id.toLowerCase() === id.toLowerCase()
    );
    this.leftPanelListFilter = existing
      ? { mode: 'id', id }
      : { mode: 'text', text: displayname };
    this.leftPipelineRouteNameList = [];
    this.leftPipelineNotFound = false;
    this.leftPipelineSearchForm.patchValue({ routename: displayname }, { emitEvent: false });
  }

  onLeftNgPipelineCleared(): void {
    this.leftPipelineRouteNameList = [];
    this.leftPipelineNotFound = false;
    this.isPipelineLeftSearchLoading = false;
    this.leftPipelinePendingSelection = null;
    this.leftPanelListFilter = { mode: 'none' };
  }

  /** Left nav list — filtered while search is active; all blocks when search is cleared. */
  getFilteredPipelineBlocks(): RoutingPipelineBlock[] {
    const blocks = this.store.pipelineBlocks;
    const filter = this.leftPanelListFilter;
    if (filter.mode === 'none') {
      return blocks;
    }
    if (filter.mode === 'id') {
      return blocks.filter((b) => b.id.toLowerCase() === filter.id.toLowerCase());
    }
    const text = filter.text.trim().toLowerCase();
    if (!text) {
      return blocks;
    }
    return blocks.filter((b) => {
      const label = (b.label ?? '').toLowerCase();
      const blockId = (b.id ?? '').toLowerCase();
      return label.includes(text) || blockId.includes(text);
    });
  }

  formatPipelineSuggestion(displayname: string): string {
    return (displayname ?? '').toString();
  }

  private resolveLeftPipelinePendingId(): string {
    if (this.leftPipelinePendingSelection?.id) {
      return this.leftPipelinePendingSelection.id.trim();
    }
    const raw = this.leftPipelineSearchForm.get('routename')?.value;
    if (raw && typeof raw === 'object' && (raw as { id?: string }).id) {
      return ((raw as { id?: string }).id ?? '').toString().trim();
    }
    const text = (raw ?? '').toString().trim();
    if (!text) {
      return '';
    }
    const uuidRe =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidRe.test(text)) {
      return text;
    }
    const fromParens = text.match(/\(([0-9a-f-]{36})\)\s*$/i);
    return fromParens ? fromParens[1] : '';
  }

  private applyLabelsToBlocks(): void {
    for (const b of this.store.pipelineBlocks) {
      const dn = this.pipelineIdToDisplayName.get(b.id);
      if (dn) {
        b.label = dn;
      }
    }
    this.store.patchBlocks([...this.store.pipelineBlocks]);
  }

  getPipelineBlockLabel(block: { id?: string; label?: string } | null | undefined): string {
    return (block?.label ?? block?.id ?? '').toString();
  }

  private primeFieldMapsFromRules(): void {
    for (const block of this.store.pipelineBlocks) {
      for (const rule of block.rules || []) {
        const persisted = (rule.field ?? '').toString().trim();
        if (!persisted) {
          continue;
        }
        const display = this.displayKeyForRuleField(persisted);
        if (display && display !== persisted) {
          this.store.fieldDisplayToPath.set(display, persisted);
        }
      }
    }
  }

  private displayKeyForRuleField(persisted: string): string {
    if (this.store.isHl7 || isHl7PersistedFieldValue(persisted)) {
      return hl7DisplayKeyFromPersistedPath(persisted) || '';
    }
    return this.pathResolver.displayKeyFromPersistedPath(persisted);
  }

  private resolvePipelineDisplayNames(ids: string[]): void {
    const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
    if (!unique.length) {
      return;
    }
    const requests = unique.map((id) =>
      this.commonService.getapiurl(`api/Routes/GetRouteList?id=${encodeURIComponent(id)}`).pipe(
        map((row: unknown) => {
          const r = row as Record<string, unknown>;
          const normalizedId = (r?.id ?? r?.routeid ?? r?.RouteId ?? id).toString().trim();
          const name = (r?.displayname ?? '').toString().trim();
          return normalizedId
            ? { id: normalizedId, displayname: name ? `${name} (${normalizedId})` : normalizedId }
            : null;
        }),
        catchError(() => of(null))
      )
    );
    forkJoin(requests).subscribe((rows: ({ id: string; displayname: string } | null)[]) => {
      for (const r of rows) {
        if (r) {
          this.pipelineIdToDisplayName.set(r.id, r.displayname);
        }
      }
      for (const b of this.store.pipelineBlocks) {
        const dn = this.pipelineIdToDisplayName.get(b.id);
        if (dn) {
          b.label = dn;
        }
      }
      this.store.patchBlocks([...this.store.pipelineBlocks]);
    });
  }
}
